import BigNumber from 'bignumber.js'

import type { FieldOf } from '@/api/types'

/**
 * Reading and writing number field values.
 *
 * Everything here goes through `BigNumber` rather than a JavaScript number. A
 * number field may hold up to ten decimal places and integers beyond
 * `Number.MAX_SAFE_INTEGER`, both of which a double silently rounds -- and the
 * rounding shows up as a cell that changes value when it is opened and closed.
 *
 * The separator names are shared with the backend, which stores the choice and
 * formats the same values for exports.
 */

export type NumberField = FieldOf<'number'>

/** How a number is punctuated, as the backend names the combinations. */
export const NUMBER_SEPARATOR_FORMATS = [
  '',
  'SPACE_COMMA',
  'SPACE_PERIOD',
  'COMMA_PERIOD',
  'PERIOD_COMMA',
] as const

export type NumberSeparatorFormat = (typeof NUMBER_SEPARATOR_FORMATS)[number]

interface SeparatorPair {
  thousand: string
  decimal: string
}

const SEPARATORS: Record<NumberSeparatorFormat, SeparatorPair> = {
  '': { thousand: '', decimal: '.' },
  SPACE_COMMA: { thousand: ' ', decimal: ',' },
  SPACE_PERIOD: { thousand: ' ', decimal: '.' },
  COMMA_PERIOD: { thousand: ',', decimal: '.' },
  PERIOD_COMMA: { thousand: '.', decimal: ',' },
}

/** What the backend accepts; a value with more places is refused, not rounded. */
export const NUMBER_MAX_DECIMAL_PLACES = 10

export interface NumberFormatOptions {
  thousandSeparator: string
  decimalSeparator: string
  prefix: string
  suffix: string
  decimalPlaces: number
  allowNegative: boolean
}

export function numberFormatOptions(field: Partial<NumberField>): NumberFormatOptions {
  const separator = field.number_separator
  const { thousand, decimal } =
    separator !== undefined && separator !== null && separator in SEPARATORS
      ? SEPARATORS[separator]
      : SEPARATORS['']

  return {
    thousandSeparator: thousand,
    decimalSeparator: decimal,
    prefix: field.number_prefix ?? '',
    suffix: field.number_suffix ?? '',
    decimalPlaces: field.number_decimal_places ?? 0,
    allowNegative: field.number_negative ?? false,
  }
}

/**
 * Reads a number the user typed, or null when it is not one.
 *
 * Tolerant on purpose: the prefix, the suffix and any thousand separators are
 * stripped, and everything after the first decimal separator is treated as the
 * fractional part. That is what lets somebody paste `$1,234.50` or `1.234,50`
 * into a cell configured either way and get the number they meant.
 *
 * @param roundDecimals Rounds to the field's configured places. Off when reading
 *   a filter value, because the backend compares against the unrounded number
 *   and rounding here would make the two disagree at the boundary.
 */
export function parseNumberValue(
  field: Partial<NumberField>,
  value: string | number | BigNumber | null | undefined,
  roundDecimals = true,
): BigNumber | null {
  if (value === null || value === undefined || value === '') return null

  const { prefix, suffix, decimalSeparator } = numberFormatOptions(field)

  const toBigNumber = (raw: BigNumber.Value): BigNumber =>
    roundDecimals
      ? new BigNumber(new BigNumber(raw).decimalPlaces(field.number_decimal_places ?? 0))
      : new BigNumber(raw)

  if (typeof value === 'number' || BigNumber.isBigNumber(value)) {
    return toBigNumber(value)
  }

  let result = value
  let isNegative = false

  if (result.startsWith('-')) {
    // A field that does not allow negatives reads `-5` as `5` rather than
    // refusing it, which is what the backend does with the same input.
    isNegative = field.number_negative ?? false
    result = result.slice(1)
  }
  if (prefix !== '' && result.startsWith(prefix)) {
    result = result.slice(prefix.length)
  }
  if (suffix !== '' && result.endsWith(suffix)) {
    result = result.slice(0, result.length - suffix.length)
  }

  // Everything that is not a digit goes, except the first decimal separator --
  // which means grouping marks are dropped wherever they are, and a second
  // separator is treated as one more of them.
  const firstSeparator = result.indexOf(decimalSeparator)
  const nonDigits = /[^0-9]/g
  if (firstSeparator === -1) {
    result = result.replace(nonDigits, '')
  } else {
    const whole = result.slice(0, firstSeparator).replace(nonDigits, '')
    const fraction = result.slice(firstSeparator + 1).replace(nonDigits, '')
    result = `${whole}${decimalSeparator}${fraction === '' ? '0' : fraction}`
  }
  if (decimalSeparator !== '.') {
    result = result.split(decimalSeparator).join('.')
  }

  const parsed = toBigNumber(result)
  if (parsed.isNaN()) return null
  return isNegative ? parsed.negated() : parsed
}

/**
 * Renders a number the way the field is configured to show it.
 *
 * @param withThousandSeparator Off while a cell is being edited, so the grouping
 *   marks do not jump around under the cursor as digits are typed.
 * @param roundDecimals See `parseNumberValue`.
 */
export function formatNumberValue(
  field: Partial<NumberField>,
  value: string | number | BigNumber | null | undefined,
  withThousandSeparator = true,
  roundDecimals = true,
): string | null {
  if (value === null || value === undefined || value === '') return ''

  const { thousandSeparator, decimalSeparator, prefix, suffix, decimalPlaces } =
    numberFormatOptions(field)

  const numeric =
    typeof value === 'string' ? parseNumberValue(field, value, roundDecimals) : value
  if (numeric === null) return null

  const big = new BigNumber(numeric)
  if (big.isNaN()) return String(NaN)

  const isNegative = big.isNegative()

  // `Intl` does the grouping and the padding, but only ever in a locale's own
  // punctuation, so a locale is picked for its decimal mark and its grouping
  // mark is then substituted for the configured one.
  const usesComma = decimalSeparator === ','
  const locale = usesComma ? 'it-IT' : 'en-US'
  const localeGrouping = usesComma ? '.' : ','

  const formatter = new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: roundDecimals ? decimalPlaces : NUMBER_MAX_DECIMAL_PLACES,
    useGrouping: true,
  })

  let formatted = formatter.format(big.absoluteValue().toString() as unknown as number)

  if (!withThousandSeparator) {
    formatted = formatted.split(localeGrouping).join('')
  } else if (thousandSeparator !== localeGrouping) {
    formatted = formatted.split(localeGrouping).join(thousandSeparator)
  }

  const sign = isNegative ? '-' : ''
  return `${sign}${prefix}${formatted}${suffix}`.trim()
}

/**
 * Renders a decimal string as the backend sends it.
 *
 * The backend serialises a number field as a string with a period for the
 * decimal mark, whatever the field is configured to display -- reading it as a
 * JavaScript number first is what loses the precision the string was carrying.
 */
export function formatDecimalNumber(
  field: Partial<NumberField>,
  value: string | number | null | undefined,
): string {
  if (value === null || value === undefined || value === '') return ''
  return formatNumberValue(field, new BigNumber(value)) ?? ''
}
